<template>
  <section class="table-of-contents">
    <LoadingOverlay v-if="isLoading" />
    <template>
      <div class="book-description">
        <div ref="description" v-html="bookDescription" />
        <p class="book-description-ending">
          {{ $t('components.books.table-of-contents.book-description-ending') }}
          <TextToSpeech
            :text="
              $t('components.books.table-of-contents.book-description-ending')
            "
          />
        </p>
      </div>

      <VideoPlayer v-if="hasVideo" :src="videoUrl" class="book-video" />

      <h2 class="table-of-contents-heading">
        {{ $t('components.books.table-of-contents.heading') }}
      </h2>
      <p
        v-if="!isLoading && chapters.length === 0"
        class="missing-chapters-message"
      >
        {{ $t('components.books.table-of-contents.missing-chapters-message') }}
      </p>
      <ol ref="tableOfContentsList" class="table-of-contents-list">
        <li
          v-for="(chapter, index) in chapters"
          :key="chapter.id"
          class="table-of-contents-list-item"
        >
          <RouterLink
            :to="{
              name: 'book-chapter',
              params: {
                chapterSlug: createSlug(chapter.name, chapter.id),
              },
            }"
            class="table-of-contents-link"
          >
            <div class="table-of-contents-link-content">
              <h3 class="table-of-contents-link-heading">
                {{ formatNumber(index + 1) }}. {{ chapter.name }}
                <TextToSpeech :text="chapter.name" />
              </h3>
              <div
                ref="chapterDescriptions"
                class="table-of-contents-link-description"
                v-html="chapter.description"
              />
              <div class="table-of-contents-link-meta">
                <ContentTypeCounts
                  class="student-content-counts"
                  :data="getContentCounts(chapter)"
                />
                <ContentTypeCounts
                  class="teacher-content-counts"
                  :data="getTeacherContentCounts(chapter)"
                />
              </div>
            </div>
            <FaIcon icon="chevron-right" class="table-of-contents-link-icon" />
          </RouterLink>
        </li>
      </ol>
    </template>
  </section>
</template>

<script>
import ContentTypeCounts from '@/components/books/ContentTypeCounts'
import FaIcon from '@/components/ui/FaIcon'
import LoadingOverlay from '@/components/LoadingOverlay'
import TextToSpeech from '@/components/accessibility/TextToSpeech'
import VideoPlayer from '@/components/media/VideoPlayer'

import textToSpeechMixin from '@/mixins/inject-text-to-speech'
import typesetMathMlMixin from '@/mixins/typeset-mathml'

import { ContentType } from '@/enums'
import { formatNumber } from '@/utils/number'
import { createSlug, getIdFromSlug } from '@/utils/string'

import { BookStoreNamespacedTypes } from '@/store/books'

export default {
  name: 'TableOfContents',
  mixins: [textToSpeechMixin, typesetMathMlMixin],
  components: {
    ContentTypeCounts,
    FaIcon,
    LoadingOverlay,
    TextToSpeech,
    VideoPlayer,
  },
  props: {
    bookSlug: {
      type: String,
      required: true,
    },
  },
  computed: {
    /**
     * @return {number}
     */
    id() {
      return getIdFromSlug(this.bookSlug)
    },

    /**
     * @returns {Object}
     */
    book() {
      return this.$store.getters[BookStoreNamespacedTypes.getters.GET_BOOK](
        this.id
      )
    },

    /**
     * @returns {string}
     */
    bookDescription() {
      return this.book.description
    },

    /**
     * @returns {boolean}
     */
    hasVideo() {
      return !!this.book.video
    },

    /**
     * @returns {string|null}
     */
    videoUrl() {
      return this.hasVideo ? this.book.video.url : null
    },

    /**
     * @return {Array<Object>}
     */
    chapters() {
      return this.$store.getters[BookStoreNamespacedTypes.getters.GET_CHAPTERS](
        this.id
      )
    },

    /**
     * @returns {boolean}
     */
    isLoading() {
      return this.book === null
    },
  },
  methods: {
    createSlug,
    formatNumber,

    /**
     * Returns an array of counts for the general content types derived from the given chapter.
     * @param {Object} chapter
     * @returns {Array<{count: number, contentType: ContentType}>}
     */
    getContentCounts(chapter) {
      return [
        {
          contentType: ContentType.LESSON,
          count: chapter.lessonsCount || 0,
        },
        {
          contentType: ContentType.SELF_TRAINING,
          count: chapter.testsCount || 0,
        },
        {
          contentType: ContentType.TRIAL_EXAM,
          count: chapter.trialExamCount || 0,
        },
      ]
    },

    /**
     * Returns an array of counts for the teacher related content types derived from the given chapter.
     * @param {Object} chapter
     * @returns {Array<{count: number, contentType: ContentType}>}
     */
    getTeacherContentCounts(chapter) {
      return [
        {
          contentType: ContentType.HOMEWORK,
          count: chapter.homeworkCount || 0,
        },
        {
          contentType: ContentType.PDF_ASSIGNMENT,
          count: chapter.pdfAssignmentsCount || 0,
        },
        {
          contentType: ContentType.ACTIVITY,
          count: chapter.activitiesCount || 0,
        },
      ]
    },

    updateTts() {
      if (this.$refs.description) {
        this.injectTextToSpeech(this.$refs.description, 'book-description')
      }

      if (this.$refs.chapterDescriptions) {
        this.injectTextToSpeech(
          this.$refs.chapterDescriptions,
          'chapter-description'
        )
      }
    },
  },
  mounted() {
    this.updateTts()
  },
  update() {
    this.updateTts()
  },
}
</script>

<style lang="scss" scoped>
.table-of-contents {
  position: relative;
  background: $grayLighter;
  flex-basis: 0;
  flex-grow: 999;
}

.book-description {
  padding: var(--content-padding-large);
  font-size: $copyFontSize;
  color: $blueSemiDark;
  text-align: center;
  background: $white;

  ::v-deep p {
    margin: 0.5em 0;
  }

  .book-description-ending {
    font-size: 1.75em;
    font-family: $handFont;
  }
}

.book-video {
  margin: var(--content-holder-margin) auto;
  max-width: calc(100% - var(--content-holder-margin) * 2);
  background: $white;
}

.table-of-contents-heading {
  margin: 1.5em 0;
  font-size: 2.5em;
  text-align: center;
  font-weight: $fontWeightSemiBold;
  color: $blueSemiDark;
}

.missing-chapters-message {
  text-align: center;
  font-size: $copyFontSize;
}

.table-of-contents-list {
  margin: var(--content-holder-margin);
  border-radius: var(--border-radius);
  font-size: $copyFontSize;
  overflow: hidden;
}

.table-of-contents-list-item {
  list-style: none;
  margin-left: 0;

  &:not(:last-child) {
    margin-bottom: 2px;
  }
}

.table-of-contents-link {
  --border-radius: var(--border-radius);

  display: flex;
  padding: var(--content-padding-small) var(--content-padding-large);
  background: $white;

  @media (hover: hover) {
    transition: background var(--transition-fast) ease-out;

    .table-of-contents-link-icon {
      transition: transform var(--transition-fast) ease-out;
    }

    &:hover {
      text-decoration: none;

      .table-of-contents-link-heading {
        text-decoration: underline;
      }

      .table-of-contents-link-icon {
        transform: translateX(5%);
      }
    }
  }
}

.table-of-contents-link-content {
  flex-grow: 1;
}

.table-of-contents-link-icon {
  flex: 0 0 5em;
  align-self: center;
  font-size: 2em;
  color: $blueSemiDark;
}

.table-of-contents-link-heading {
  color: $blueSemiDark;
  font-size: 1.5em;
}

.table-of-contents-link-meta {
  display: flex;
  gap: 0 3em;
  flex-wrap: wrap;
}

.student-content-counts,
.teacher-content-counts {
  display: inline-block;
  border-radius: var(--border-radius-medium);
}

.student-content-counts {
  background: $grayLighter;
}

.teacher-content-counts {
  border: 1px dashed $blue;
}

@media (max-width: $minWidthTablet) {
  .table-of-contents-link-icon {
    flex: 0 0 2em;
  }
}

@media (max-width: 30rem) {
  .table-of-contents-heading {
    font-size: 2em;
  }

  .book-video,
  .table-of-contents-list {
    margin: var(--content-holder-margin) 0;
    max-width: 100%;
    border-radius: 0;
  }
}

@media (max-width: $minWidthSmallTablet) {
  .table-of-contents-link-description {
    display: none;
  }
}
</style>
